/**
 * Escape-to-close + focus handling for a modal dialog (deep review #6).
 *
 * TASK-662's completion criteria require every new control to be keyboard-operable. The epic's
 * two new modals — `CaptureModeChooser` and `RescheduleModal` — set `role="dialog"`
 * `aria-modal="true"` and label their controls, but neither closed on Escape nor moved focus
 * into the dialog, so a keyboard user had to tab blind through the page behind the overlay to
 * reach Cancel. The repo already had the Escape idiom inline in four other modals
 * (`TransactionDetailModal`, `ReviewDecisionModal`, `DocumentRequestForm`, `PortfolioComposer`);
 * this is that idiom in one place rather than a fifth copy.
 *
 * Returns a ref to put on the dialog element. On mount it focuses the dialog (so the next Tab
 * lands inside it) and on unmount it restores focus to whatever opened the modal — otherwise
 * closing the dialog drops the caret back to the top of the document.
 *
 * ponytail: no focus TRAP. Tab can still leave the dialog, which the four existing modals also
 * allow; a real trap needs a tested implementation and belongs with an a11y ticket that migrates
 * all six, not smuggled in here.
 */
import { useEffect, useRef } from 'react';

export function useModalDismiss<T extends HTMLElement = HTMLDivElement>(onClose: () => void) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, [onClose]);

  return ref;
}

export default useModalDismiss;
